import { HeroSection } from "@/components/sections/HeroSection";
import { ResultsSection } from "@/components/sections/ResultsSection";
import { ProcessSection } from "@/components/sections/ProcessSection";
import { FAQSection } from "@/components/sections/FAQSection";
import { ContactSection } from "@/components/sections/ContactSection";
import { CaseStudyTabs } from "@/components/sections/CaseStudyTabs";
import { IndustriesSection } from "@/components/sections/IndustriesSection";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarDialog } from "@/components/CalendarDialog";
import {
  Globe,
  MapPin,
  Star,
  Megaphone,
  Cpu,
  Mail,
  ArrowRight,
  CheckCircle,
  Users,
  TrendingUp,
  Award,
} from "lucide-react";

const servicesOverview = [
  {
    icon: Globe,
    title: "SMART Websites",
    description:
      "Fast, mobile-first websites built to turn visitors into enquiries, bookings and phone calls for your trade or service business.",
    href: "/services/websites",
  },
  {
    icon: MapPin,
    title: "Google Business Profile & Local SEO",
    description:
      "Get found first when locals search for your services. Dominate Google Maps and local search results.",
    href: "/services/gbp-seo",
  },
  {
    icon: Star,
    title: "Reputation & Reviews",
    description:
      "Automated review collection that builds trust and social proof on autopilot — 5-star reviews, consistently.",
    href: "/services/reputation",
  },
  {
    icon: Megaphone,
    title: "Lead Generation — Google & Facebook Ads",
    description:
      "Targeted Google & Facebook ad campaigns that bring quality leads ready to book — not tyre-kickers.",
    href: "/services/lead-generation",
  },
  {
    icon: Cpu,
    title: "Automation & AI Tools",
    description:
      "Missed-call text-back, CRM automations, and AI chatbots that respond instantly — 24/7.",
    href: "/services/automation",
  },
  {
    icon: Mail,
    title: "Reactivation Campaigns",
    description:
      "Win back past customers with smart email & SMS campaigns. High ROI from your existing database.",
    href: "/services/reactivation",
  },
];

const whyDifferent = [
  {
    icon: Users,
    title: "Built for Local Service Businesses",
    description:
      "We focus on job-based, location-driven trades and service businesses. Not generic marketers — we know exactly what it takes to win work.",
  },
  {
    icon: TrendingUp,
    title: "Results You Can Measure",
    description:
      "Real improvements in Google visibility, website traffic, and booked jobs. Transparent reporting lets you track growth week by week.",
  },
  {
    icon: Award,
    title: "Complete Digital Growth System",
    description:
      "Websites, Local SEO, Google & Facebook Ads, and reviews — all working together as one connected system. No scattered tactics, no wasted effort.",
  },
];

const siteUrl = "https://fullscalemarketing.com.au";
const siteName = "FullScale Marketing";
const siteTitle = "FullScale Marketing | Digital Marketing for Trades & Service Businesses";
const siteDescription =
  "Digital marketing for Australian tradies and local service businesses — websites, Google reviews, Local SEO and ads that grow visibility, enquiries and booked jobs.";
const siteImage = "https://fullscalemarketing.com.au/og-image.jpg";

const Index = () => {
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: siteName,
    url: siteUrl,
    logo: `${siteUrl}/og-image.jpg`,
    image: siteImage,
    description:
      "Digital marketing agency for Australian trade and local service businesses.",
    sameAs: [
      "https://www.instagram.com/fullscale_marketing/",
      "https://www.facebook.com/p/FullScale-Marketing-61562089223781/",
    ],
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "MarketingAgency",
    "@id": `${siteUrl}/#organization`,
    name: siteName,
    alternateName: "FullScale Marketing Australia",
    description: siteDescription,
    url: siteUrl,
    logo: `${siteUrl}/logo.png`,
    image: siteImage,
    telephone: "+61",
    email: "info@fullscalemarketing.com.au",
    address: { "@type": "PostalAddress", addressCountry: "AU", addressRegion: "Australia-wide" },
    areaServed: [
      { "@type": "Country", name: "Australia" },
      { "@type": "City", name: "Sydney" },
      { "@type": "City", name: "Melbourne" },
      { "@type": "City", name: "Brisbane" },
      { "@type": "City", name: "Perth" },
      { "@type": "City", name: "Adelaide" },
    ],
    priceRange: "$$",
  };

  return (
    <>
      <Helmet>
        <title>{siteTitle}</title>
        <meta name="description" content={siteDescription} />
        <meta
          name="keywords"
          content="marketing for tradies, digital marketing for tradies, marketing for local service businesses, local SEO Australia, Google Ads for tradies, Facebook Ads for tradies, marketing for electricians, marketing for plumbers, marketing for cleaners, marketing for gyms, marketing for salons, marketing for pest control"
        />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="author" content={siteName} />
        <link rel="canonical" href={`${siteUrl}/`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${siteUrl}/`} />
        <meta property="og:title" content={siteTitle} />
        <meta property="og:description" content={siteDescription} />
        <meta property="og:image" content={siteImage} />
        <meta property="og:locale" content="en_AU" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={siteTitle} />
        <meta name="twitter:description" content={siteDescription} />
        <meta name="twitter:image" content={siteImage} />
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
      </Helmet>

      {/* Hero */}
      <HeroSection />

      {/* Why FullScale is Different */}
      <section className="section-padding bg-muted relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-40" />
        <div className="container-wide relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl mx-auto text-center mb-8 sm:mb-10"
          >
            <div className="section-badge mb-3 sm:mb-4 text-xs">
              <span>Why FullScale</span>
            </div>
            <h2 className="heading-section text-foreground mb-3 text-balance">
              Why Trade & Service Businesses Choose{" "}
              <span className="text-gradient">FullScale Marketing</span>
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-[1.65]">
              We're not a generic agency. Everything we build is designed around how
              location-dependent, job-based businesses actually win work.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-4 sm:gap-6 max-w-4xl mx-auto">
            {whyDifferent.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="card-lift bg-card rounded-xl p-5 sm:p-6 border border-border/60 shadow-card text-center"
              >
                <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center mx-auto mb-3">
                  <item.icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-base sm:text-lg font-bold text-foreground mb-2">
                  {item.title}
                </h3>
                <p className="text-muted-foreground text-[0.9375rem] leading-[1.65]">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="section-padding bg-background relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-25" />
        <div className="container-wide relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl mx-auto text-center mb-8 sm:mb-10"
          >
            <div className="section-badge mb-3 sm:mb-4 text-xs">
              <span>Our Services</span>
            </div>
            <h2 className="heading-section text-foreground mb-3 text-balance">
              Everything You Need to{" "}
              <span className="text-gradient">Dominate Your Local Market</span>
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-[1.65]">
              A modular growth system — pick what you need now and scale when you're ready.
            </p>
          </motion.div>

          <div className="flex flex-col sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {servicesOverview.map((service, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06, duration: 0.4 }}
              >
                <Link
                  to={service.href}
                  className="group flex items-center gap-4 sm:flex-col sm:items-start bg-card rounded-xl p-4 sm:p-6 border border-border/60 shadow-card hover:shadow-xl hover:border-primary/30 transition-all duration-300 h-full"
                >
                  <div className="w-12 h-12 sm:w-10 sm:h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                    <service.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between sm:block">
                      <h3 className="text-[0.9375rem] sm:text-lg font-bold text-foreground group-hover:text-primary transition-colors sm:mb-2">
                        {service.title}
                      </h3>
                      <ArrowRight className="w-4 h-4 text-primary shrink-0 sm:hidden" />
                    </div>
                    <p className="hidden sm:block text-muted-foreground text-[0.9375rem] leading-[1.65] mb-3">
                      {service.description}
                    </p>
                    <span className="hidden sm:inline-flex items-center gap-1.5 text-primary text-sm font-semibold group-hover:gap-2.5 transition-all">
                      Learn more
                      <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="text-center mt-8"
          >
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-primary font-semibold text-base hover:underline underline-offset-4"
            >
              View all services
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Process Overview - Simplified 3 Steps */}
      <section className="section-padding bg-muted relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-40" />
        <div className="container-wide relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl mx-auto text-center mb-8 sm:mb-10"
          >
            <div className="section-badge mb-3 sm:mb-4 text-xs">
              <span>How It Works</span>
            </div>
            <h2 className="heading-section text-foreground mb-3 text-balance">
              Simple. Proven. Built for{" "}
              <span className="text-gradient">Trade & Service Businesses</span>
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              {
                step: "01",
                title: "Strategy Call",
                text: "We audit your digital presence and build a custom growth plan for your trade or service business.",
              },
              {
                step: "02",
                title: "We Build & Launch",
                text: "Our team sets up your website, SEO, ads, reviews, and automations — you focus on the tools.",
              },
              {
                step: "03",
                title: "Enquiries Roll In",
                text: "Qualified enquiries start coming through, and we optimise monthly so results keep compounding.",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12, duration: 0.5 }}
                className="card-lift relative bg-card rounded-xl p-5 sm:p-6 border border-border/60 shadow-card text-center"
              >
                <span className="absolute top-3 right-4 text-4xl font-bold text-primary/10">
                  {item.step}
                </span>
                <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-3 font-bold text-sm">
                  {item.step}
                </div>
                <h3 className="text-base sm:text-lg font-bold text-foreground mb-2">
                  {item.title}
                </h3>
                <p className="text-muted-foreground text-[0.9375rem] leading-[1.65]">
                  {item.text}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-8"
          >
            <CalendarDialog
              buttonVariant="default"
              buttonSize="lg"
              buttonClassName="h-12 text-sm font-bold rounded-xl"
            />
          </motion.div>
        </div>
      </section>

      <IndustriesSection />

      <CaseStudyTabs />

      {/* Results / Testimonials */}
      <ResultsSection />

      {/* Lead magnet — Free GBP Audit */}
      <section className="section-padding bg-muted relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-40" />
        <div className="container-wide relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto bg-card rounded-2xl border border-primary/20 shadow-card p-6 sm:p-10 text-center"
          >
            <div className="section-badge mb-3 sm:mb-4 text-xs">
              <span>Free — No Call Required</span>
            </div>
            <h2 className="heading-section text-foreground mb-3 text-balance">
              Not ready to chat? <span className="text-gradient">Get your Free GBP Audit</span>
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-[1.65] mb-6">
              60-second AI audit. See your local ranking heatmap, your Google Business Profile score, and the exact fixes to climb the Map Pack — no email gymnastics.
            </p>
            <Link
              to="/free-gbp-audit"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-bold px-6 sm:px-8 h-12 rounded-xl shadow-button hover:shadow-button-hover hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 text-sm sm:text-[15px]"
            >
              Get My Free GBP Audit
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <FAQSection />

      {/* Final CTA */}
      <ContactSection />
    </>
  );
};

export default Index;
